import { Button, Label, Select, TextInput, Modal } from 'flowbite-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useEffect, useState, useContext } from 'react';
import { useMutationData } from '../../../hooks/useMutationData';
import { useGetData } from '../../../hooks/useGetData';
import { UsuarioInterface } from '../../../interfaces/UsuarioInterface';
import { RolInterface } from '../../../interfaces/RolInterface';
import { Loading } from '../../../components/Loading/Loading';
import { ThemeContext } from '../../../components/ThemeContext/ThemeContext';

export const UsuarioEditarScreen = () => {
  const { usuario_id } = useParams();
  const { control, handleSubmit, reset } = useForm();
  const mutateUsuario = useMutationData({
    url: `/usuario/${usuario_id}`,
    reload: ['usuario', `${usuario_id}`],
  });
  const queryUsuario = useGetData<UsuarioInterface>({
    url: `/usuario/${usuario_id}?includeRol=true`,
    name: ['usuario', `${usuario_id}`],
  });
  const queryRoles = useGetData<RolInterface[]>({
    url: `/rol?estatus[eq]=1`,
    name: ['rol'],
  });
  const navigate = useNavigate();
  const { isDarkMode } = useContext(ThemeContext); // Accede al contexto del tema

  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (queryUsuario.data) {
      reset({
        correo: queryUsuario.data.correo || '',
        nombre: queryUsuario.data.nombre || '',
        apellido_materno: queryUsuario.data.apellido_materno || '',
        apellido_paterno: queryUsuario.data.apellido_paterno || '',
        rol_id: queryUsuario.data.rol.id || '',
      });
    }
  }, [queryUsuario.data, reset]);

  if (queryUsuario.isLoading || mutateUsuario.isPending || queryRoles.isLoading) {
    return <Loading />;
  }

  return (
    <div className={`min-h-screen p-6 ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'}`}>
      <div className={`max-w-4xl mx-auto p-6 rounded-lg shadow-lg space-y-6 ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
        <div className={`bg-gradient-to-r from-blue-400 to-blue-600 p-6 text-white rounded-t-lg`}>
          <h2 className="text-3xl font-bold text-center">Editar Usuario</h2>
        </div>
        <form
          onSubmit={handleSubmit((data) => {
            if (!data.contrasena) {
              delete data.contrasena;
            }
            mutateUsuario.mutate(
              {
                data: data,
                tipo: 'patch',
              },
              {
                onSuccess: () => {
                  navigate('/usuario');
                },
              }
            );
          })}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="nombre" value="Nombre(s)" />
              <Controller
                name="nombre"
                control={control}
                defaultValue=""
                rules={{
                  required: 'Se requiere un nombre',
                  maxLength: {
                    value: 32,
                    message: 'El nombre debe tener menos de 32 caracteres',
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextInput
                    id="nombre"
                    placeholder="Nombre..."
                    {...field}
                    color={fieldState.error ? 'failure' : 'default'}
                    helperText={fieldState.error?.message}
                    className={isDarkMode ? 'bg-gray-700 text-black' : 'bg-white text-black'}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="apellido_paterno" value="Apellido Paterno" />
              <Controller
                name="apellido_paterno"
                control={control}
                defaultValue=""
                rules={{
                  required: 'Se requiere un apellido paterno',
                  maxLength: {
                    value: 32,
                    message: 'El apellido paterno debe tener menos de 32 caracteres',
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextInput
                    id="apellido_paterno"
                    placeholder="Apellido..."
                    {...field}
                    color={fieldState.error ? 'failure' : 'default'}
                    helperText={fieldState.error?.message}
                    className={isDarkMode ? 'bg-gray-700 text-black' : 'bg-white text-black'}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="apellido_materno" value="Apellido Materno" />
              <Controller
                name="apellido_materno"
                control={control}
                defaultValue=""
                rules={{
                  maxLength: {
                    value: 32,
                    message: 'El apellido materno debe tener menos de 32 caracteres',
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextInput
                    id="apellido_materno"
                    placeholder="Apellido..."
                    {...field}
                    color={fieldState.error ? 'failure' : 'default'}
                    helperText={fieldState.error?.message}
                    className={isDarkMode ? 'bg-gray-700 text-black' : 'bg-white text-black'}
                  />
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="correo" value="Correo" />
              <Controller
                name="correo"
                control={control}
                defaultValue=""
                rules={{
                  required: 'Se requiere un correo',
                  maxLength: {
                    value: 128,
                    message: 'El correo debe tener menos de 128 caracteres',
                  },
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Correo inválido',
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextInput
                    id="correo"
                    type="email"
                    placeholder="correo@ejemplo.com"
                    {...field}
                    color={fieldState.error ? 'failure' : 'default'}
                    helperText={fieldState.error?.message}
                    className={isDarkMode ? 'bg-gray-700 text-black' : 'bg-white text-black'}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contrasena" value="Contraseña" />
              <Controller
                name="contrasena"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="contrasena"
                    type="password"
                    placeholder="************"
                    {...field}
                    color={fieldState.error ? 'failure' : 'default'}
                    helperText={fieldState.error?.message}
                    className={isDarkMode ? 'bg-gray-700 text-black' : 'bg-white text-black'}
                  />
                )}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="rol_id" value="Rol" />
            <Controller
              name="rol_id"
              control={control}
              defaultValue=""
              rules={{ required: 'Se requiere un rol' }}
              render={({ field }) => (
                <Select id="rol_id" {...field} className={isDarkMode ? 'bg-gray-700 text-black' : 'bg-white text-black'}>
                  <option value="">Elige un Rol</option>
                  {queryRoles.data?.map((rol) => (
                    <option key={rol.id} value={rol.id}>
                      {rol.nombre}
                    </option>
                  ))}
                </Select>
              )}
            />
          </div>

          <div className="flex justify-center pt-4 space-x-4">
            <Button
              type="button"
              className={`w-1/2 ${isDarkMode ? 'bg-red-700' : 'bg-red-600'} hover:${isDarkMode ? 'bg-red-800' : 'bg-red-700'} text-white`}
              onClick={() => navigate('/usuario')}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className={`w-1/2 ${isDarkMode ? 'bg-green-700' : 'bg-green-600'} hover:${isDarkMode ? 'bg-green-800' : 'bg-green-700'} text-white`}
            >
              Guardar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};